import React, { useState, useRef } from 'react';
import { Download, Search, RefreshCw, Database, AlertCircle, FileText } from 'lucide-react';

const EXAMPLE_QUERIES = [
  "Contacts where Enrolled_Date__c in LAST_N_DAYS:30",
  "Enrollment__c where Status__c = 'Complete' grouped by state",
  "Contacts where Dropped_Date__c != null and Dropped_Date__c >= 2024-01-01",
  "itPayment__c where Amount__c > 500 and paymentStatus__c = 'Completed'",
  "Enrollment__c where Name LIKE '%5600%' and Status__c = 'Complete'",
  "Field_Experience__c for Certification_State__c = 'VA'",
];

export default function SFQuery() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const runQuery = async (q) => {
    const queryText = q || question;
    if (!queryText.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/.netlify/functions/sf-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: queryText.trim() }),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        setError(`Server error (${response.status}). The function may have timed out — try a simpler query.`);
        return;
      }

      if (!response.ok) {
        setError(data.error || `Server error (${response.status})`);
        if (data.generatedQuery) {
          setResult({ query: data.generatedQuery, columns: [], records: [], totalRecords: 0 });
        }
        return;
      }

      setResult(data);
    } catch (err) {
      setError(err.message || 'Network error — the function may have timed out.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      runQuery();
    }
  };

  const handleExampleClick = (example) => {
    setQuestion(example);
    runQuery(example);
  };

  const downloadCSV = () => {
    if (!result || !result.records.length) return;

    const csvEscape = (val) => {
      const str = val == null ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const lines = [
      result.columns.map(csvEscape).join(','),
      ...result.records.map(rec =>
        result.columns.map(col => csvEscape(rec[col])).join(',')
      ),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sf-query-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sf-query-tab">
      {/* Custom Reports */}
      <div className="sf-query-reports-section">
        <h3 className="sf-query-reports-title">Custom Reports</h3>
        <div className="sf-query-reports-list">
          <a
            href="/.netlify/functions/la-enrolled-no-test"
            className="sf-query-report-link"
            download
          >
            <FileText size={16} />
            <div>
              <span className="sf-query-report-name">LA Enrolled - No Passed Tests - Has $99 Payments</span>
              <span className="sf-query-report-desc">Louisiana enrolled + active contacts with no passed cert tests and at least one completed $99 payment</span>
            </div>
            <Download size={14} />
          </a>
          <a
            href="/.netlify/functions/la-enrolled-inactive"
            className="sf-query-report-link"
            download
          >
            <FileText size={16} />
            <div>
              <span className="sf-query-report-name">LA Enrolled - No Passed Tests - No Login 6mo - No Notes</span>
              <span className="sf-query-report-desc">Louisiana enrolled + active, no passed cert tests, no portal login in 6 months, no notes on record</span>
            </div>
            <Download size={14} />
          </a>
        </div>
      </div>

      {/* Query Input */}
      <div className="sf-query-input-section">
        <div className="sf-query-input-row">
          <div className="sf-query-input-wrapper">
            <Database size={18} className="sf-query-input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="sf-query-input"
              placeholder="Ask a question about your Salesforce data..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
          </div>
          <button
            className="sf-query-submit"
            onClick={() => runQuery()}
            disabled={loading || !question.trim()}
          >
            {loading ? <RefreshCw size={16} className="spinning" /> : <Search size={16} />}
            <span>{loading ? 'Querying...' : 'Query'}</span>
          </button>
        </div>

        {/* Example Queries */}
        {!result && !loading && (
          <div className="sf-query-examples">
            <p className="sf-query-examples-label">Try an example:</p>
            <div className="sf-query-examples-list">
              {EXAMPLE_QUERIES.map((ex, i) => (
                <button
                  key={i}
                  className="sf-query-example-chip"
                  onClick={() => handleExampleClick(ex)}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="sf-query-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="sf-query-results">
          {/* Generated SOQL */}
          <div className="sf-query-soql">
            <span className="sf-query-soql-label">Generated SOQL:</span>
            <code className="sf-query-soql-code">{result.query}</code>
          </div>

          {/* Result Header */}
          <div className="sf-query-results-header">
            <span className="sf-query-results-count">
              {result.totalRecords} record{result.totalRecords !== 1 ? 's' : ''} returned
            </span>
            {result.records.length > 0 && (
              <button className="sf-query-download" onClick={downloadCSV}>
                <Download size={14} />
                <span>Download CSV</span>
              </button>
            )}
          </div>

          {/* Results Table */}
          {result.records.length > 0 && (
            <div className="sf-query-table-wrapper">
              <table className="sf-query-table">
                <thead>
                  <tr>
                    <th className="sf-query-th-row">#</th>
                    {result.columns.map(col => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.records.map((rec, i) => (
                    <tr key={i}>
                      <td className="sf-query-td-row">{i + 1}</td>
                      {result.columns.map(col => (
                        <td key={col}>{rec[col] == null ? '' : String(rec[col])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
